import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Fields, Sources } from '../generator/types';
import { getLetterTypeLabel } from '../generator/utils';
import {
  resolveParagraphContact,
  resolveParagraphs,
  resolveParagraphText,
} from './blockResolver';
import { BlockStack } from './BlockStack';
import { DiscardConfirmModal } from './DiscardConfirmModal';
import { DrawerPdfLetterProps } from './DrawerPdfLetter';
import {
  button,
  drawerHandle,
  drawerShell,
  modalBackdrop,
  modalContainer,
  primaryButton,
} from './drawerStyles';
import { readFieldValue } from './fieldKeys';
import { generateDrawerLetter } from './generateDrawerLetter';
import { resolveToPlainText } from './HtmlMarkerRenderer';
import {
  applyInAdditionPrefix,
  collectFieldPlaceholders,
  resolveOrderedBlocks,
} from './letterBodyBuilder';
import { LetterPreviewModal } from './LetterPreviewModal';
import { SourcePanel } from './SourcePanel';
import { LetterDrawerContextValue } from './types';
import { UseLetterDrawerReturn } from './useLetterDrawer';
import { getUnfilledFields } from './validation';

export interface LetterDrawerProps {
  drawer: UseLetterDrawerReturn;
}

const COLLAPSED_HEIGHT = 44;
const MIN_EXPANDED_HEIGHT = 200;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

export const LetterDrawer = ({ drawer }: LetterDrawerProps) => {
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [resizedHeight, setResizedHeight] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  // Timestamp of last successful Copy click — drives the temporary
  // "Copied to clipboard" label on the Copy button.
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const [hoveredButton, setHoveredButton] = useState<
    'copy' | 'preview' | 'upload' | null
  >(null);

  useEffect(() => {
    if (copiedAt === null) return undefined;
    const t = window.setTimeout(() => setCopiedAt(null), 2000);
    return () => window.clearTimeout(t);
  }, [copiedAt]);

  const attemptClose = useCallback(() => {
    if (drawer.isDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    drawer.close();
  }, [drawer]);

  useEffect(() => {
    if (!drawer.isOpen || !drawer.isDirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [drawer.isOpen, drawer.isDirty]);

  const headerLabel = drawer.letterType
    ? getLetterTypeLabel(drawer.letterType)
    : 'Letter';

  const buildPdfProps = useCallback((): DrawerPdfLetterProps | null => {
    if (!drawer.letterType || !drawer.carrier) return null;
    return {
      letterType: drawer.letterType,
      pricingMode: drawer.pricingMode,
      isJuvenile: drawer.isJuvenile,
      carrier: drawer.carrier,
      carrierDisplayName: drawer.carrierDisplayName,
      policyNumber: drawer.policyNumber,
      recipientFullName: drawer.recipientFullName,
      proposedInsuredName: drawer.proposedInsuredName,
      riders: drawer.riders,
      selectedRiders: drawer.selectedRiders,
      selectedSources: drawer.selectedSources,
      fieldValues: drawer.fieldValues,
      hasFcra: drawer.fcraEnabled,
    };
  }, [drawer]);

  const hasContent =
    drawer.selectedSources.length > 0 || drawer.selectedRiders.length > 0;

  const unfilledFields = useMemo(
    () =>
      getUnfilledFields({
        letterType: drawer.letterType,
        pricingMode: drawer.pricingMode,
        selectedSources: drawer.selectedSources,
        isJuvenile: drawer.isJuvenile,
        fieldValues: drawer.fieldValues,
      }),
    [
      drawer.letterType,
      drawer.pricingMode,
      drawer.selectedSources,
      drawer.isJuvenile,
      drawer.fieldValues,
    ],
  );

  const isLetterUsable = hasContent && unfilledFields.length === 0;

  const handleUpload = useCallback(async () => {
    const props = buildPdfProps();
    if (!props) return;
    setUploading(true);
    try {
      const files = await generateDrawerLetter(props);
      drawer.uploadFiles(files);
      drawer.close();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to upload letter', e);
    } finally {
      setUploading(false);
    }
  }, [buildPdfProps, drawer]);

  const handleCopy = useCallback(() => {
    const props = buildPdfProps();
    if (!props) return;
    const { orderedBlocks } = resolveOrderedBlocks(
      props.letterType,
      props.pricingMode,
      props.selectedSources,
    );
    const placeholders = collectFieldPlaceholders(orderedBlocks);

    const resolve = (text: string, blockId: string) =>
      resolveToPlainText(
        text,
        props.fieldValues,
        placeholders,
        props.selectedRiders,
        props.carrierDisplayName,
        blockId,
      );

    // Card data only: walk the selected blocks in order and emit their
    // paragraphs (honoring user overrides + "In addition," for blocks 2+).
    // Pre/post fixed text (intro, FCRA rights notice, etc.) lives in the PDF
    // and is intentionally NOT included here.
    const parts: string[] = [];
    orderedBlocks.forEach((block, blockIdx) => {
      if (block.id === Sources.custom) {
        const custom = (
          readFieldValue(props.fieldValues, Fields.customContent, block.id) ??
          ''
        ).trim();
        if (custom) parts.push(custom);
        return;
      }
      const paragraphs = resolveParagraphs(block, props.isJuvenile);
      paragraphs.forEach((p, paraIdx) => {
        const paraKey = `__para_${block.id}_${paraIdx}`;
        const contactKey = `__contact_${block.id}_${paraIdx}`;
        const overrideText = props.fieldValues[paraKey];
        let text: string;
        if (overrideText !== undefined) {
          text = overrideText;
        } else {
          const rawText = resolveParagraphText(p, props.isJuvenile);
          const withPrefix = applyInAdditionPrefix(rawText, blockIdx, paraIdx);
          text = resolve(withPrefix, block.id);
        }
        if (text.trim()) parts.push(text);

        const contactOverride = props.fieldValues[contactKey];
        const contactRaw =
          contactOverride !== undefined
            ? contactOverride
            : resolveParagraphContact(p, props.isJuvenile);
        if (contactRaw) {
          const contact =
            contactOverride !== undefined
              ? contactOverride
              : resolve(contactRaw, block.id);
          if (contact.trim()) parts.push(contact);
        }
      });
    });

    const text = parts.join('\n\n');
    if (!text) return;
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedAt(Date.now());
  }, [buildPdfProps]);

  const pdfProps = useMemo(buildPdfProps, [buildPdfProps]);

  // Resolve current expanded height (resized override > default 68vh).
  const expandedHeight =
    resizedHeight ??
    (typeof window !== 'undefined'
      ? Math.round(window.innerHeight * 0.68)
      : 600);
  const currentHeight = drawer.isExpanded ? expandedHeight : COLLAPSED_HEIGHT;

  // Pad the page bottom so the case content can scroll past the drawer.
  useEffect(() => {
    if (!drawer.isOpen) return undefined;
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = `${currentHeight}px`;
    return () => {
      document.body.style.paddingBottom = prev;
    };
  }, [drawer.isOpen, currentHeight]);

  // Global mouse listeners while resizing.
  useEffect(() => {
    if (!isResizing) return undefined;
    const onMove = (e: MouseEvent) => {
      const next = window.innerHeight - e.clientY;
      const maxHeight = window.innerHeight - 60;
      setResizedHeight(clamp(next, MIN_EXPANDED_HEIGHT, maxHeight));
    };
    const onUp = () => setIsResizing(false);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizing]);

  if (!drawer.isOpen) return null;
  if (typeof document === 'undefined') return null;

  const height = drawer.isExpanded ? expandedHeight : COLLAPSED_HEIGHT;

  return createPortal(
    <>
      <div style={{ ...drawerShell, height }}>
        {drawer.isExpanded ? (
          <div
            onMouseDown={e => {
              e.preventDefault();
              setIsResizing(true);
            }}
            title="Drag to resize"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              cursor: 'ns-resize',
              zIndex: 2,
              background: 'transparent',
            }}
          />
        ) : null}
        <div style={drawerHandle} onClick={drawer.toggleExpanded}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 3,
                borderRadius: 2,
                background: '#B8B7AE',
              }}
            />
            <span style={{ fontSize: 13, fontWeight: 500, color: '#1A1917' }}>
              {headerLabel}
            </span>
            <span
              style={{
                fontSize: 10,
                fontFamily: 'monospace',
                background: '#F0EFE9',
                border: '1px solid #D8D7CE',
                borderRadius: 4,
                padding: '1px 6px',
                color: '#6B6A62',
              }}
            >
              {drawer.selectedSources.length} block
              {drawer.selectedSources.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              style={{
                ...button,
                background:
                  hoveredButton === 'copy' ? '#F0EFE9' : button.background,
                borderColor:
                  hoveredButton === 'copy' ? '#B8B7AE' : button.borderColor,
              }}
              disabled={!isLetterUsable}
              onClick={handleCopy}
              onMouseEnter={() => setHoveredButton('copy')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              {copiedAt !== null ? 'Copied to clipboard' : 'Copy'}
            </button>
            <button
              type="button"
              style={{
                ...button,
                background:
                  hoveredButton === 'preview' ? '#F0EFE9' : button.background,
                borderColor:
                  hoveredButton === 'preview' ? '#B8B7AE' : button.borderColor,
              }}
              disabled={!isLetterUsable}
              onClick={() => setPreviewOpen(true)}
              onMouseEnter={() => setHoveredButton('preview')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Preview
            </button>
            <button
              type="button"
              style={{
                ...primaryButton,
                background:
                  hoveredButton === 'upload'
                    ? '#3A3937'
                    : primaryButton.background,
                borderColor:
                  hoveredButton === 'upload'
                    ? '#3A3937'
                    : primaryButton.borderColor,
              }}
              disabled={!isLetterUsable || isUploading}
              onClick={handleUpload}
              onMouseEnter={() => setHoveredButton('upload')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              {isUploading ? 'Uploading…' : 'Upload letter'}
            </button>
            <button
              type="button"
              style={{ ...button, padding: '5px 10px' }}
              onClick={attemptClose}
              aria-label="Close drawer"
            >
              ×
            </button>
          </div>
        </div>

        {drawer.isExpanded ? (
          <div
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: '268px 1fr',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <SourcePanel drawer={drawer} />
            <div
              data-letter-block-stack
              style={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
                minWidth: 0,
                background: '#F5F4F0',
              }}
            >
              {hasContent && unfilledFields.length > 0 ? (
                <div
                  role="alert"
                  style={{
                    margin: '12px 20px 0',
                    padding: '10px 14px',
                    border: '1px solid #FCA5A5',
                    background: '#FEF2F2',
                    borderRadius: 6,
                    color: '#991B1B',
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    fontWeight: 500,
                    flexShrink: 0,
                  }}
                >
                  {unfilledFields.length === 1
                    ? '1 required field needs to be filled before you can preview or upload:'
                    : `${unfilledFields.length} required fields need to be filled before you can preview or upload:`}{' '}
                  <span style={{ fontWeight: 600 }}>
                    {unfilledFields.map((f, i) => (
                      <React.Fragment key={`${f.blockId}:${f.key}`}>
                        {i > 0 ? ', ' : ''}
                        <button
                          type="button"
                          onClick={() => {
                            const card = document.getElementById(
                              `block-${f.blockId}`,
                            );
                            if (!card) return;
                            card.dispatchEvent(
                              new CustomEvent('nora-block-expand'),
                            );
                            card.scrollIntoView({
                              behavior: 'smooth',
                              block: 'nearest',
                            });
                            setTimeout(() => {
                              const span = card.querySelector<HTMLElement>(
                                `[data-field="${f.key}"]`,
                              );
                              if (span) {
                                span.focus();
                                const range = document.createRange();
                                range.selectNodeContents(span);
                                const sel = window.getSelection();
                                sel?.removeAllRanges();
                                sel?.addRange(range);
                                return;
                              }
                              const dateInput =
                                card.querySelector<HTMLInputElement>(
                                  `[data-field-input="${f.key}"]`,
                                );
                              dateInput?.focus();
                            }, 300);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: '#991B1B',
                            fontWeight: 600,
                            fontSize: 'inherit',
                            textDecoration: 'underline',
                          }}
                          title={
                            f.reason === 'empty'
                              ? `Required: ${f.placeholder}`
                              : f.reason
                          }
                        >
                          {f.placeholder}
                          {unfilledFields.some(
                            o => o !== f && o.placeholder === f.placeholder,
                          )
                            ? ` (${f.blockLabel})`
                            : ''}
                          {f.reason !== 'empty' ? (
                            <span
                              style={{
                                fontWeight: 400,
                                textDecoration: 'none',
                                marginLeft: 4,
                              }}
                            >
                              — {f.reason}
                            </span>
                          ) : null}
                        </button>
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              ) : null}
              <BlockStack drawer={drawer as LetterDrawerContextValue} />
            </div>
          </div>
        ) : null}
      </div>

      <DiscardConfirmModal
        pendingLetterType={drawer.pendingLetterType}
        onConfirm={drawer.confirmLetterTypeSwitch}
        onCancel={drawer.cancelLetterTypeSwitch}
      />

      {closeConfirmOpen ? (
        <div style={modalBackdrop} onClick={() => setCloseConfirmOpen(false)}>
          <div
            style={{ ...modalContainer, maxWidth: 460 }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '22px 24px 12px' }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                Discard letter?
              </h3>
              <p style={{ fontSize: 13, color: '#6B6A62', lineHeight: 1.5 }}>
                You have unsaved edits. Closing the drawer will discard them.
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                padding: '12px 24px 18px',
              }}
            >
              <button
                type="button"
                style={button}
                onClick={() => setCloseConfirmOpen(false)}
              >
                Keep editing
              </button>
              <button
                type="button"
                style={primaryButton}
                onClick={() => {
                  setCloseConfirmOpen(false);
                  drawer.close();
                }}
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <LetterPreviewModal
        open={isPreviewOpen}
        onClose={() => setPreviewOpen(false)}
        pdfProps={pdfProps}
      />
    </>,
    document.body,
  );
};
